import React, { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
  ClickAwayListener,
  Container,
  Grow,
  IconButton,
  MenuItem,
  Paper,
  PopoverProps,
  Popper,
  Stack,
  TextField,
} from '@mui/material'
import {
  DownloadDone,
  KeyboardArrowDown,
  KeyboardArrowUp,
  RadioButtonChecked,
  Search,
  ThumbsUpDown,
} from '@mui/icons-material'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { TableHeader } from '@tiptap/extension-table-header'

import { color } from '@trivie/ui-web/theme'
import { Text } from '@trivie/ui-web/components/text'
import { Button } from '@trivie/ui-web/components/button'
import { Switch } from '@trivie/ui-web/components/switch/switch'
import { MediaPlayer } from '@trivie/ui-web/components/media-player/media-player'

import { TListItem } from './nodes/list/list-item'
import { TParagraph } from './nodes/paragraph/paragraph'
import { TTableRow } from './nodes/table/table-row'
import { TTable } from './nodes/table/table'
import { TTableCell } from './nodes/table/table-cell'
import './editor.css'
import { isYoutube } from '@trivie/core/src/utils/mimetype'

const Mark = require('mark.js')

interface DocumentViewerProps {
  quiz: any
  onboarding?: any
}

export const DocumentViewer = observer((props: DocumentViewerProps) => {
  const { onboarding, quiz } = props

  const [showSearch, setShowSearch] = useState(false)
  const [selectedText, setSelectedText] = useState('')
  const [selectedNodes, setSelectedNodes] = useState<any>([])
  const [openSelectionMenu, setOpenSelectionMenu] = React.useState(false)
  const [alreadyOpen, setAlreadyOpen] = useState(false)
  const [anchorEl, setAnchorEl] = React.useState<any['anchorEl']>(null)
  const previousAnchorElPosition = React.useRef<DOMRect | undefined>(undefined)

  useEffect(() => {
    if (anchorEl) {
      if (typeof anchorEl === 'object') {
        previousAnchorElPosition.current = anchorEl.getBoundingClientRect()
      } else {
        previousAnchorElPosition.current = anchorEl()?.getBoundingClientRect()
      }
    }
  }, [anchorEl])

  const handleScroll = () => {
    setOpenSelectionMenu(false)
  }

  const pm = document.querySelector('.ProseMirror') as any
  useEffect(() => {
    // Hide selection menu on scroll
    if (pm) {
      pm.addEventListener('scroll', handleScroll)
    }
    return () => document.removeEventListener('scroll', handleScroll)
  }, [pm])

  const docEditor = useEditor(
    {
      editable: false,
      onSelectionUpdate: ({ editor, transaction }) => {
        const { state } = editor
        const { selection } = transaction
        const { from, to } = selection

        // Set selected text content
        const sel = state.doc.textBetween(from, to, '')
        setSelectedText(sel)

        // Recursively gather node ids between selection positions
        const nodes = [] as any
        state.doc.nodesBetween(from, to, (node) => {
          if (node.attrs.node_id) {
            nodes.push(node.attrs.node_id)
          }
        })

        setSelectedNodes(nodes)
      },
      editorProps: {
        attributes: {
          class: 'DocViewer',
        },
      },
      extensions: [
        StarterKit.configure({
          gapcursor: false,
          heading: { levels: [1, 2, 3] },
          paragraph: false,
          listItem: false,
        }),
        TListItem.configure({ quiz, selectQuestion: (q) => quiz?.selectCitation(q) } as any),
        TTable.configure({ quiz, selectQuestion: (q) => quiz?.selectCitation(q) } as any),
        TParagraph.configure({
          quiz,
          selectQuestion: (q) => quiz?.selectCitation(q),
          selectTimestamp: (value) => {
            playerRef.current.props.onPlay()
            playerRef.current.seekTo(value / 1000, 'seconds', true)
          },
        } as any),
        TTableCell.configure({
          HTMLAttributes: { class: 'document-table-cell' },
          quiz,
          selectQuestion: (q) => quiz?.selectCitation(q),
        } as any),
        TTableRow.configure({
          HTMLAttributes: {
            class: 'document-table-row',
          },
          quiz,
          selectQuestion: (q) => quiz?.selectCitation(q),
        } as any),
        TableHeader.configure({
          HTMLAttributes: {
            class: 'document-table-header',
          },
        }),
      ],
      content: quiz?.selectedDocument?.nodes ?? {},
    },
    [],
  )

  useEffect(() => {
    if (quiz?.selectedDocument && docEditor) {
      setTimeout(() => {
        docEditor?.commands.setContent({
          type: 'doc',
          content: quiz?.selectedDocument.nodes.content,
        })
      }, 0)
    }
  }, [
    quiz?.questions?.length,
    quiz?.generatingQuestions,
    quiz?.generatingNodes.length,
    quiz?.selectedDocument,
    quiz?.stopGenerating,
    quiz?.questions[0]?.generated_question_id,
  ])

  const handleCloseSelectionMenu = () => {
    setOpenSelectionMenu(false)
  }

  const selection = window.getSelection()
  const handleMouseUp = ({ clientX: x, clientY: y }) => {
    if (onboarding) {
      return
    }

    setAlreadyOpen(false)

    // Skip if selection has a length of 0
    if (!selection || selection.anchorOffset === selection.focusOffset) {
      handleCloseSelectionMenu()
      return
    }

    const getBoundingClientRect = () => {
      if (selection.rangeCount === 0 && previousAnchorElPosition.current) {
        handleCloseSelectionMenu()
        return previousAnchorElPosition.current
      }
      return selection.getRangeAt(0).getBoundingClientRect()
    }
    setOpenSelectionMenu(true)

    setAnchorEl({ getBoundingClientRect } as any)
  }

  const generateFromSelection = (type) => {
    if (docEditor && selectedNodes) {
      quiz?.set('showSettings', false)
      selectedNodes.map((id) => {
        document.querySelector(`[node_id="${id}"]`)?.classList.add('GeneratingFact')
      })
      const generatingNodes = quiz?.generatingNodes || []
      quiz.set('generatingNodes', [...generatingNodes, ...selectedNodes])
      quiz
        ?.generateQuestions(
          quiz?.mercuryResource.resource_id,
          1,
          [type],
          selectedNodes,
          selectedNodes.length > 1 || selectedText.length > 1000,
        )
        .then((data) => {
          quiz.set(
            'generatingNodes',
            quiz?.generatingNodes.filter((n) => !selectedNodes.includes(Number(n))),
          )
          quiz?.selectQuestion(quiz?.questions[quiz?.questions.length - 1])
        })
        .finally(() => {
          selectedNodes.map((id) => {
            document.querySelector(`[node_id="${id}"]`)?.classList.remove('GeneratingFact')
          })
        })

      if (window.getSelection) {
        window.getSelection()?.removeAllRanges()
      } else if (document.getSelection) {
        document.getSelection()?.empty()
      }
      setSelectedNodes([])
    }
  }

  const attachResource = (e) => {
    if (e.target.checked) {
      quiz.addResource(quiz.mercuryResource)
    } else {
      quiz.removeResource(quiz.mercuryResource)
    }
  }

  const [keywords, setKeywords] = useState<any>()

  const markInstance = new Mark(document.querySelector('.ProseMirror'))
  const search = (keyword) =>
    markInstance.unmark({
      done: function () {
        markInstance.mark(keyword.trim(), {
          separateWordSearch: false,
          accuracy: 'complementary',
          diacritics: true,
          debug: false,
          done: () => {
            setKeywords(document.querySelectorAll('mark'))
          },
        })
      },
    })

  const [selectedKeyword, setSelectedKeyword] = useState()

  const page = (next: boolean) => {
    if (keywords && keywords.length > 0) {
      const idx = keywords?.length === 1 ? 0 : next ? 1 : -1
      const selected = keywords[Array.prototype.slice.call(keywords).indexOf(selectedKeyword) + idx]
      keywords.forEach((mark) => mark.setAttribute('class', ''))
      if (selected) {
        selected.setAttribute('class', 'SelectedKeyword')
        selected.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'center' })
        setSelectedKeyword(selected)
      }
    }
  }

  const onSearchChange = (e) => {
    search(e.target.value)
  }

  const playerRef = useRef() as any
  const attached = quiz?.resources?.find((r) => r.source_id === quiz?.mercuryResource.source_id)
  const id = openSelectionMenu ? 'virtual-element-popover' : undefined

  return (
    <div
      className="resourceContainer"
      style={{ height: '100%', flexDirection: 'column', display: 'flex', overflow: 'hidden' }}
    >
      <div style={TOOLBAR}>
        {(quiz?.mercuryResource?.converted_pdf_url || quiz?.mercuryResource?.filename_or_url) && (
          <a
            style={PREVIEW}
            target="_blank"
            rel="noopener noreferrer"
            href={
              quiz.mercuryResource?.filename_or_url
                ? quiz.mercuryResource?.filename_or_url
                : quiz.mercuryResource?.converted_pdf_url
                  ? quiz.mercuryResource?.converted_pdf_url
                  : ''
            }
          >
            <Text style={{ color: color.blue }} text="Preview" variant="body1Medium" />
          </a>
        )}
        <IconButton
          sx={{
            svg: { color: showSearch ? color.blue : color.black },
          }}
          onClick={() => {
            setShowSearch(!showSearch)
            setTimeout(() => {
              const input = document.getElementById('search-field') as any
              if (input) {
                input.focus()
              }
            }, 200)
          }}
        >
          <Search style={{ fontSize: 22 }} htmlColor={color.shade70} />
        </IconButton>
        <div style={ATTACH}>
          {!onboarding && (
            <>
              <Text
                style={{ marginRight: 8 }}
                text="Display Source To Users"
                variant="body2Medium"
              />
              <Switch
                color="success"
                checked={Boolean(attached)}
                onChange={(e) => attachResource(e)}
                size="small"
              />
            </>
          )}
        </div>
      </div>
      {showSearch && (
        <div style={{ paddingLeft: 24, paddingRight: 24, marginBottom: 12 }}>
          <TextField
            id="search-field"
            type="text"
            name="keyword"
            variant="standard"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                page(true)
              }
            }}
            fullWidth
            InputProps={{
              style: { paddingRight: 90 },
              startAdornment: (
                <Search style={{ marginRight: 12, fontSize: 22 }} htmlColor={color.shade70} />
              ),
              endAdornment: (
                <div style={INPUT_SEARCH}>
                  <Text
                    style={{ marginRight: 8 }}
                    text={`${
                      !keywords || keywords?.length === 0
                        ? 0
                        : keywords?.length === 1
                          ? 1
                          : Array.prototype.slice.call(keywords).indexOf(selectedKeyword) + 1
                    } of ${keywords?.length ?? 0}`}
                    variant="body1"
                  />
                  <div style={{ flexDirection: 'column', display: 'flex' }}>
                    <IconButton sx={{ top: 2, padding: 0 }} onClick={() => page(false)}>
                      <KeyboardArrowUp style={{ fontSize: 14 }} htmlColor={color.shade70} />
                    </IconButton>
                    <IconButton sx={{ bottom: 2, padding: 0 }} onClick={() => page(true)}>
                      <KeyboardArrowDown style={{ fontSize: 14 }} htmlColor={color.shade70} />
                    </IconButton>
                  </div>
                </div>
              ),
            }}
            style={{ fontSize: 16, fontWeight: 600 }}
            placeholder="Search"
            onChange={onSearchChange}
          />
        </div>
      )}
      <Popper
        id={id}
        style={{ display: alreadyOpen ? 'none' : '', zIndex: 9999 }}
        open={openSelectionMenu}
        anchorEl={anchorEl}
        role={undefined}
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            timeout={{ appear: 200, enter: 200, exit: 0 }}
            {...TransitionProps}
            style={{
              zIndex: 9999,
              transformOrigin: placement === 'bottom' ? 'center top' : 'center left',
            }}
          >
            <Paper style={DROPDOWN} variant="outlined">
              <ClickAwayListener onClickAway={handleCloseSelectionMenu}>
                <Stack>
                  <MenuItem
                    style={{
                      gap: 12,
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                      height: 40,
                    }}
                    onClick={() => generateFromSelection('fill_in_the_blank')}
                  >
                    <DownloadDone htmlColor={color.shade70} fontSize="small" />
                    <Text text="Generate Fill-In-The-Blank Question" variant="body1Medium" />
                  </MenuItem>
                  <MenuItem
                    style={{ gap: 12, height: 40 }}
                    onClick={() => generateFromSelection('multiple_choice')}
                  >
                    <RadioButtonChecked htmlColor={color.shade70} fontSize="small" />
                    <Text text="Generate Multiple Choice Question" variant="body1Medium" />
                  </MenuItem>
                  <MenuItem
                    style={{
                      gap: 12,
                      borderBottomLeftRadius: 12,
                      borderBottomRightRadius: 12,
                      height: 40,
                    }}
                    onClick={() => generateFromSelection('true_false')}
                  >
                    <ThumbsUpDown htmlColor={color.shade70} fontSize="small" />
                    <Text text="Generate True / False Question" variant="body1Medium" />
                  </MenuItem>
                </Stack>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      {quiz?.mercuryResource.isVideo || quiz?.mercuryResource.isAudio ? (
        <>
          <Container maxWidth="sm">
            <MediaPlayer
              thumbnail={quiz?.mercuryResource.thumbnail_url}
              onScreenshot={
                !quiz?.mercuryResource?.isYoutube &&
                !quiz?.mercuryResource?.isVimeo &&
                quiz?.mercuryResource?.isVideo
                  ? (screenshot) => {
                      quiz?.selectedQuestion?.setImage(screenshot)
                    }
                  : undefined
              }
              variant={quiz?.mercuryResource.isVideo ? 'video' : 'audio'}
              onProgress={(state) => quiz?.mercuryResource?.set('progress', state)}
              defaultProgress={quiz?.mercuryResource?.progress}
              onError={() => {}}
              url={quiz?.mercuryResource.filename_or_url}
              ref={playerRef}
            />
          </Container>
          <Text
            component="div"
            ml={3}
            text="Transcript:"
            variant="body2Emphasized"
            mt={2}
            mb={'4px'}
            sx={{ color: color.shade50 }}
          />
        </>
      ) : null}
      <div
        id="editor"
        className="editorContainer"
        style={EDITOR}
        key="doc-viewer"
        onMouseDown={() => {
          if (openSelectionMenu) {
            setAlreadyOpen(true)
          }
        }}
        onMouseUp={handleMouseUp}
      >
        <EditorContent editor={docEditor} />
      </div>
    </div>
  )
})

const TOOLBAR: React.CSSProperties = {
  flexDirection: 'row',
  alignItems: 'center',
  display: 'flex',
  paddingLeft: 24,
  paddingRight: 24,
  justifyContent: 'space-between',
  marginBottom: 16,
}
const PREVIEW: React.CSSProperties = {
  border: `1px solid ${color.blue}`,
  paddingTop: 8,
  paddingBottom: 8,
  paddingRight: 16,
  paddingLeft: 16,
  borderRadius: 12,
  background: color.white,
  textDecoration: 'none',
  marginRight: 12,
}
const ATTACH: React.CSSProperties = {
  flexDirection: 'row',
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  justifyContent: 'flex-end',
}
const INPUT_SEARCH: React.CSSProperties = {
  alignItems: 'center',
  flexDirection: 'row',
  display: 'flex',
  position: 'absolute',
  right: 0,
}
const DROPDOWN: React.CSSProperties = {
  zIndex: 9999,
  position: 'relative',
  borderRadius: 12,
  boxShadow: '0px 4px 8px 8px rgba(0,0,0,0.08)',
}
const EDITOR: React.CSSProperties = {
  paddingBottom: 24,
  minHeight: 0,
  height: '100%',
  flexDirection: 'column',
  display: 'flex',
}
